import React, { useState } from "react";
import { UserX, Lock } from "lucide-react";

interface LeaveOrganizationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  organizationName: string;
}

const LeaveOrganizationModal: React.FC<LeaveOrganizationModalProps> = ({
  open,
  onClose,
  onConfirm,
  organizationName,
}) => {
  const [step, setStep] = useState<"initial" | "confirm">("initial");

  const handlePrimaryConfirm = () => {
    setStep("confirm");
  };

  const handleFinalConfirm = () => {
    onConfirm();
  };

  const handleBackToInitial = () => {
    setStep("initial");
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-background-paper dark:bg-background-paperDark p-6 rounded-lg shadow-lg w-full max-w-lg">
        {step === "initial" && (
          <>
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-xl font-bold text-text-primary dark:text-text-primaryDark">
                Confirm Leaving Organization
              </h2>
              <button
                onClick={onClose}
                aria-label="Close"
                className="text-xl text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark transition"
              >
                ✕
              </button>
            </div>

            <div className="flex items-center mb-6 bg-background-panel dark:bg-background-panelDark rounded-lg p-4">
              <UserX className="w-12 h-12 text-red-500 mr-4" />
              <div>
                <p className="text-lg font-semibold text-text-primary dark:text-text-primaryDark">
                  Bon voyage! Leaving so soon?
                </p>
              </div>
            </div>

            <p className="text-sm text-text-secondary dark:text-text-secondaryDark mb-6">
              You are about to leave{" "}
              <strong className="text-text-primary dark:text-text-primaryDark">
                {organizationName}
              </strong>
              . Once you leave, you will lose access to all associated data,
              members, and features. This action cannot be undone.
            </p>

            <div className="flex justify-end space-x-4">
              <button
                onClick={onClose}
                className="py-2 px-6 rounded-md text-sm font-medium border border-divider dark:border-divider-dark text-text-secondary dark:text-text-secondaryDark hover:bg-background-panel dark:hover:bg-background-panelDark transition"
              >
                Cancel
              </button>
              <button
                onClick={handlePrimaryConfirm}
                className="py-2 px-6 rounded-md text-sm font-medium bg-red-500 text-white hover:bg-red-600 transition"
              >
                Confirm Leave
              </button>
            </div>
          </>
        )}

        {step === "confirm" && (
          <>
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-xl font-bold text-text-primary dark:text-text-primaryDark">
                Final Confirmation
              </h2>
              <button
                onClick={onClose}
                aria-label="Close"
                className="text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark transition"
              >
                ✕
              </button>
            </div>

            <div className="flex items-center mb-6 bg-background-panel dark:bg-background-panelDark rounded-lg p-4">
              <Lock className="w-12 h-12 text-red-500 mr-4" />

              <div>
                <p className="text-lg font-semibold text-text-primary dark:text-text-primaryDark">
                  This action cannot be undone.
                </p>
              </div>
            </div>

            <p className="text-sm text-text-secondary dark:text-text-secondaryDark mb-6">
              By proceeding, you will permanently leave{" "}
              <strong className="text-text-primary dark:text-text-primaryDark">
                {organizationName}
              </strong>
              . You will need to be re-invited to regain access.
            </p>

            <div className="flex justify-between space-x-4">
              <button
                onClick={handleBackToInitial}
                className="py-2 px-6 rounded-md text-sm font-medium border border-divider dark:border-divider-dark text-text-secondary dark:text-text-secondaryDark hover:bg-background-panel dark:hover:bg-background-panelDark transition"
              >
                Back
              </button>
              <button
                onClick={handleFinalConfirm}
                className="py-2 px-6 rounded-md text-sm font-medium bg-red-500 text-white hover:bg-red-600 transition"
              >
                Confirm Leave
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LeaveOrganizationModal;
